function renderFirstColumnDefLayoutTemplate({
  sortLabelExpr,
  sortableHeaderButtonJsx
}: {
  sortLabelExpr: string
  sortableHeaderButtonJsx: string
}): string {
  return `header: ({ table, column }) => {
      ${sortLabelExpr}
      return (
        <div className="flex min-w-0 items-center gap-4 pl-1">
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
          ${sortableHeaderButtonJsx}
        </div>
      )
    }`
}

function renderFirstColumnDefLayoutDetailsTemplate({
  columnHeader
}: {
  columnHeader: string
}): string {
  return `header: ({ table }) => (
      <div className="flex items-center gap-4 pl-1">
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
        <span>${columnHeader}</span>
      </div>
    )`
}

function renderFirstColumnDefLayoutGenerateAuthorshipValueTemplate({
  generateAuthorshipValue
}: {
  generateAuthorshipValue: string
}): string {
  return `cell: ({ row }) => {
      ${generateAuthorshipValue}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          {label ? (
            <span className="text-sm font-medium truncate max-w-[300px]" title={actor?.email}>
              {label}
            </span>
          ) : (
            <span className="text-muted-foreground">Unavailable</span>
          )}
        </div>
      )
    }`
}

function renderFirstColumnDefContentTemplate({
  generateImageCellMedia
}: {
  generateImageCellMedia: string
}): string {
  return `cell: ({ row }) => {
      ${generateImageCellMedia}
    }`
}

function renderFirstColumnDefLayoutGenerateSelectLabelTemplate({
  columnAccessorKey,
  generateSelectLabel
}: {
  columnAccessorKey: string
  generateSelectLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey}
      ${generateSelectLabel}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <Badge variant="outline">
            {label || 'Unavailable'}
          </Badge>
        </div>
      )
    }`
}

function renderFirstColumnDefLayoutGenerateRelationshipLabelTemplate({
  columnAccessorKey,
  generateRelationshipLabel
}: {
  columnAccessorKey: string
  generateRelationshipLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey}
      ${generateRelationshipLabel}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <Badge variant="outline">
            {label || 'Unavailable'}
          </Badge>
        </div>
      )
    }`
}

function renderFirstColumnDefLayoutDetailsBranchTemplate({
  columnAccessorKey
}: {
  columnAccessorKey: string
}): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <Badge variant="outline">
            {value === null || value === undefined ? 'Unavailable' : String(value)}
          </Badge>
        </div>
      )
    }`
}

function renderFirstColumnDefLayoutDetailsBranchBodyTemplate({
  columnAccessorKey
}: {
  columnAccessorKey: string
}): string {
  return `cell: ({ row }) => {
      const date = new Date(row.original.${columnAccessorKey})
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <div className="text-sm">
            {date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </div>
        </div>
      )
    }`
}

function renderEmailColumnCell({ columnAccessorKey }: { columnAccessorKey: string }): string {
  return `cell: ({ row }) => {
      const email = row.original.${columnAccessorKey} as string
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="text-sm font-medium truncate max-w-[300px]">{email}</span>
        </div>
      )
    }`
}

function renderCurrencyColumnCell({ columnAccessorKey }: { columnAccessorKey: string }): string {
  return `cell: ({ row }) => {
      const amount = row.original.${columnAccessorKey} as number
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <div className="text-sm font-medium">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD'
            }).format(amount)}
          </div>
        </div>
      )
    }`
}

function renderNumberColumnCell({ columnAccessorKey }: { columnAccessorKey: string }): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey} as number
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <div className="text-sm">{value}</div>
        </div>
      )
    }`
}

function renderBooleanColumnCell({
  columnAccessorKey,
  trueLabel,
  falseLabel
}: {
  columnAccessorKey: string
  trueLabel: string
  falseLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey} as boolean
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <Badge variant={value ? 'outline' : 'secondary'}>
            {value ? ${trueLabel} : ${falseLabel}}
          </Badge>
        </div>
      )
    }`
}

function renderCustomColumnCell({ columnComponent }: { columnComponent: string }): string {
  return `cell: ({ row }) => {
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <${columnComponent} data={row.original} />
        </div>
      )
    }`
}

function renderSelectLabelCell({
  columnAccessorKey,
  generateSelectLabel
}: {
  columnAccessorKey: string
  generateSelectLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey}
      ${generateSelectLabel}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {label || 'Unavailable'}
          </span>
        </div>
      )
    }`
}

function renderRelationshipLabelCell({
  columnAccessorKey,
  generateRelationshipLabel
}: {
  columnAccessorKey: string
  generateRelationshipLabel: string
}): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey}
      ${generateRelationshipLabel}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {label || 'Unavailable'}
          </span>
        </div>
      )
    }`
}

function renderPlainValueCell({ columnAccessorKey }: { columnAccessorKey: string }): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey}
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          <span className="truncate max-w-[300px]">
            {value === null || value === undefined ? 'Unavailable' : String(value)}
          </span>
        </div>
      )
    }`
}

function renderFirstColumnDefClickHandlerTemplate({
  columnAccessorKey
}: {
  columnAccessorKey: string
}): string {
  return `cell: ({ row }) => {
      const value = row.original.${columnAccessorKey} as string
      return (
        <div className="flex items-center gap-4 h-full pl-1">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
          {value ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate block max-w-[250px]"
              title={value}
              onClick={(e) => e.stopPropagation()}
            >
              {truncateText(value, 50)}
            </a>
          ) : (
            <span className="text-muted-foreground">\u2014</span>
          )}
        </div>
      )
    }`
}

function renderFirstColumnDefContentEnabledTemplate({
  columnAccessorKey,
  columnSize,
  meta,
  headerDef,
  cellDef,
  sortableEnabled
}: {
  columnAccessorKey: string
  columnSize: string
  meta: string
  headerDef: string
  cellDef: string
  sortableEnabled: boolean
}): string {
  return `  {
    accessorKey: '${columnAccessorKey}',
    ${columnSize}${meta}${headerDef},
    ${cellDef},
    enableSorting: ${sortableEnabled},
    enableHiding: false
  }`
}

export function renderFirstColumnDef({
  columnAccessorKey,
  columnSize,
  meta,
  sortable,
  header,
  cell
}: {
  columnAccessorKey: string
  columnSize: string
  meta: string
  sortable: boolean
  header:
    | { sortable: true; sortLabelExpr: string; sortableHeaderButtonJsx: string }
    | { sortable: false; columnHeader: string }
  cell:
    | { kind: 'authorship'; generateAuthorshipValue: string }
    | { kind: 'image'; generateImageCellMedia: string }
    | { kind: 'select-badge'; columnAccessorKey: string; generateSelectLabel: string }
    | { kind: 'relationship-badge'; columnAccessorKey: string; generateRelationshipLabel: string }
    | { kind: 'badge-value'; columnAccessorKey: string }
    | { kind: 'date'; columnAccessorKey: string }
    | { kind: 'email'; columnAccessorKey: string }
    | { kind: 'currency'; columnAccessorKey: string }
    | { kind: 'number'; columnAccessorKey: string }
    | { kind: 'boolean'; columnAccessorKey: string; trueLabel: string; falseLabel: string }
    | { kind: 'custom'; columnComponent: string }
    | { kind: 'select'; columnAccessorKey: string; generateSelectLabel: string }
    | { kind: 'relationship'; columnAccessorKey: string; generateRelationshipLabel: string }
    | { kind: 'plain'; columnAccessorKey: string }
    | { kind: 'link'; columnAccessorKey: string }
}): string {
  const headerDef = header.sortable
    ? renderFirstColumnDefLayoutTemplate(header)
    : renderFirstColumnDefLayoutDetailsTemplate(header)

  let cellDef: string
  switch (cell.kind) {
    case 'authorship':
      cellDef = renderFirstColumnDefLayoutGenerateAuthorshipValueTemplate(cell)
      break
    case 'image':
      cellDef = renderFirstColumnDefContentTemplate(cell)
      break
    case 'select-badge':
      cellDef = renderFirstColumnDefLayoutGenerateSelectLabelTemplate(cell)
      break
    case 'relationship-badge':
      cellDef = renderFirstColumnDefLayoutGenerateRelationshipLabelTemplate(cell)
      break
    case 'badge-value':
      cellDef = renderFirstColumnDefLayoutDetailsBranchTemplate(cell)
      break
    case 'date':
      cellDef = renderFirstColumnDefLayoutDetailsBranchBodyTemplate(cell)
      break
    case 'email':
      cellDef = renderEmailColumnCell(cell)
      break
    case 'currency':
      cellDef = renderCurrencyColumnCell(cell)
      break
    case 'number':
      cellDef = renderNumberColumnCell(cell)
      break
    case 'boolean':
      cellDef = renderBooleanColumnCell(cell)
      break
    case 'custom':
      cellDef = renderCustomColumnCell(cell)
      break
    case 'select':
      cellDef = renderSelectLabelCell(cell)
      break
    case 'relationship':
      cellDef = renderRelationshipLabelCell(cell)
      break
    case 'plain':
      cellDef = renderPlainValueCell(cell)
      break
    case 'link':
      cellDef = renderFirstColumnDefClickHandlerTemplate(cell)
      break
  }

  return renderFirstColumnDefContentEnabledTemplate({
    columnAccessorKey,
    columnSize,
    meta,
    headerDef,
    cellDef,
    sortableEnabled: sortable
  })
}
